import { CONSTANT } from "src/app/constant";
import { CandidateRole } from "src/app/models/enum";
import {
	CandidateRating,
	DisplayCommentDraft,
	DisplayCommentItemKind,
	DisplayObDraft,
	DisplayObStatus,
	DraftItem,
	OBComments,
	OBCommentsReq,
	ResetManeuverItem,
} from "src/app/models/models";
import { shouldReplayDraftAddRemoveObEvent } from "../../shared-v2/utils/observation.utils";
import { isTs1V3OrV4RatingFormVersion } from "../../shared-v2/utils/rating-form-version.utils";
import { filterTs1Step2SummaryComments } from "../preview/ts1-preview-summary-comments.utils";
import { collectAssessmentConfigObPresets, Ts1ObPreset } from "./ts1-predefined-ob.utils";
import { OutcomeSlot, outcomeSlotCandidateRole, outcomeSlotLabel } from "./ts1-rating-outcome-interaction";

function commentItemKind(comment: OBComments): DisplayCommentItemKind {
	if (comment.isManeuverOutcome) return "maneuver_outcome";
	if (comment.isTemCompetencyOutcome) return "tem_outcome";
	if (comment.ob === "REPEAT") return "repeat";
	if (comment.isObservationComment) return "ob_note";
	if (comment.isDisabled || comment.remarks === "N/A") return "ob_na";
	if (comment.action === false) return "ob_remove";
	if (comment.action === true) return "ob_add";
	return "instructor_note";
}

export function mapSummaryCommentToDisplayComment(comment: OBComments, sortOrder: number): DisplayCommentDraft {
	return {
		sortOrder,
		candidateId: comment.candidateId ?? "",
		itemKind: commentItemKind(comment),
		maneuverCode: comment.maneuverCode,
		observationCode: comment.observationCode,
		candidateRole: comment.candidateRole,
		action: comment.action,
		rate: comment.rate,
		body: comment.remarks,
		instructorComment: comment.instructorComment,
		repeatCount: comment.repeatCount,
		isDisabled: comment.isDisabled,
		createdAt: comment.createdAt,
		ob: comment.ob,
		maneuver: comment.maneuver,
		isRepeat: comment.isRepeat,
		isManeuverOutcome: comment.isManeuverOutcome,
		isTemCompetencyOutcome: comment.isTemCompetencyOutcome,
		isObservationComment: comment.isObservationComment,
	};
}

export function mapDisplayCommentToSummaryComment(row: DisplayCommentDraft): OBComments {
	return {
		maneuver: row.maneuver ?? "",
		ob: row.ob ?? "",
		action: row.action ?? null,
		remarks: row.body,
		instructorComment: row.instructorComment ?? "",
		rate: row.rate,
		candidateId: row.candidateId,
		maneuverCode: row.maneuverCode,
		candidateRole: row.candidateRole,
		isDisabled: !!row.isDisabled,
		observationCode: row.observationCode,
		createdAt: row.createdAt,
		repeatCount: row.repeatCount,
		isRepeat: row.isRepeat,
		isManeuverOutcome: row.isManeuverOutcome ?? row.itemKind === "maneuver_outcome",
		isTemCompetencyOutcome: row.isTemCompetencyOutcome ?? row.itemKind === "tem_outcome",
		isObservationComment: row.isObservationComment ?? row.itemKind === "ob_note",
	};
}

function obDisplayKey(row: {
	candidateId?: string;
	maneuverCode?: string;
	observationCode?: string;
	candidateRole?: CandidateRole | string;
}): string {
	return [row.candidateId ?? "", row.maneuverCode ?? "", row.observationCode ?? "", row.candidateRole ?? ""].join("|");
}

function isManeuverOutcomeCascadeDescription(description?: string): boolean {
	return (
		description === CONSTANT.MANEUVER_OUTCOME_NA_CASCADE_NOTES ||
		description === CONSTANT.RESET_MANEUVER_OUTCOME_NA_CASCADE_NOTES
	);
}

function stripManeuverOutcomeCascadeEvents(events: OBCommentsReq[] | undefined): OBCommentsReq[] {
	return (events ?? []).filter((event) => !isManeuverOutcomeCascadeDescription(event.description));
}

const OUTCOME_NA_SLOTS: OutcomeSlot[] = ["pfPrimary", "pm", "pmPrimary", "pf"];

function currentManeuverOutcomeNaSeats(selectedOBs: OBComments[] | undefined): OBComments[] {
	const latest = new Map<string, OBComments>();
	for (const row of selectedOBs ?? []) {
		if (!row.isManeuverOutcome || row.isRepeat) continue;
		if (!row.candidateId || !row.maneuverCode) continue;
		const key = [row.candidateId, row.maneuverCode, row.candidateRole ?? "", row.ob ?? ""].join("|");
		const prev = latest.get(key);
		const at = row.createdAt ? new Date(row.createdAt).getTime() : 0;
		const prevAt = prev?.createdAt ? new Date(prev.createdAt).getTime() : -1;
		if (!prev || at >= prevAt) latest.set(key, row);
	}
	return [...latest.values()].filter((row) => row.isDisabled);
}

function overlayOutcomeNaOnDisplayObs(
	rows: DisplayObDraft[],
	cr: CandidateRating,
	presets: Ts1ObPreset[] = [],
): DisplayObDraft[] {
	const last = new Map(rows.map((row) => [obDisplayKey(row), row]));
	const order = rows.map((row) => obDisplayKey(row));
	const naSeats = currentManeuverOutcomeNaSeats(cr.selectedOBs);

	for (const seat of naSeats) {
		for (const [key, row] of last) {
			if (
				row.candidateId === seat.candidateId &&
				row.maneuverCode === seat.maneuverCode &&
				row.candidateRole === seat.candidateRole
			) {
				last.set(key, {
					...row,
					status: "na",
					isDisabled: true,
				});
			}
		}
	}

	for (const outcome of cr.maneuverOutcomes ?? []) {
		for (const slot of OUTCOME_NA_SLOTS) {
			if (outcome[slot] !== "na") continue;
			const snapshot = outcome.preNaObSnapshots?.[slot];
			if (!snapshot) continue;
			const role = outcomeSlotCandidateRole(slot);
			const seat =
				naSeats.find(
					(row) =>
						row.maneuverCode === outcome.maneuverCode &&
						row.candidateRole === role &&
						row.ob === outcomeSlotLabel(slot),
				) ?? naSeats.find((row) => row.maneuverCode === outcome.maneuverCode && row.candidateRole === role);
			if (!seat?.candidateId) continue;

			for (const [observationCode, attempt] of Object.entries(snapshot)) {
				const used = (attempt.totalCount ?? 0) > 0 || (attempt.currentCount ?? 0) > 0 || attempt.preDefined === true;
				if (!used) continue;
				const key = obDisplayKey({
					candidateId: seat.candidateId,
					maneuverCode: outcome.maneuverCode,
					observationCode,
					candidateRole: role,
				});
				if (!last.has(key)) order.push(key);
				last.set(key, {
					sortOrder: 0,
					candidateId: seat.candidateId,
					maneuverCode: outcome.maneuverCode,
					observationCode,
					candidateRole: role,
					status: "na",
					isDisabled: true,
					activeAttempt: attempt.currentCount ?? 0,
					totalAttempt: attempt.totalCount ?? 0,
					lastAction:
						attempt.lastAction === CONSTANT.OB_LAST_ACTION.ADDED ||
						attempt.lastAction === CONSTANT.OB_LAST_ACTION.REMOVED
							? attempt.lastAction
							: CONSTANT.OB_LAST_ACTION.NO_ACTION,
				});
			}
		}
	}

	for (const seat of naSeats) {
		if (!seat.candidateId || !seat.maneuverCode) continue;

		for (const preset of presets) {
			if (preset.maneuverCode !== seat.maneuverCode) continue;
			if (preset.candidateRole && preset.candidateRole !== seat.candidateRole) continue;
			const key = obDisplayKey({
				candidateId: seat.candidateId,
				maneuverCode: seat.maneuverCode,
				observationCode: preset.observationCode,
				candidateRole: seat.candidateRole,
			});
			if (last.has(key)) continue;
			order.push(key);
			last.set(key, {
				sortOrder: 0,
				candidateId: seat.candidateId,
				maneuverCode: seat.maneuverCode,
				observationCode: preset.observationCode,
				candidateRole: seat.candidateRole,
				status: "na",
				isDisabled: true,
				activeAttempt: preset.activeAttempt ?? 0,
				totalAttempt: preset.totalAttempt ?? 0,
				lastAction: CONSTANT.OB_LAST_ACTION.NO_ACTION,
			});
		}
	}

	return order.map((key, index) => ({
		...(last.get(key) as DisplayObDraft),
		sortOrder: index,
	}));
}

function isUnusedManeuverOutcomeCascadeEvent(event: OBCommentsReq): boolean {
	const description = event.description ?? "";
	const isCascade =
		description === CONSTANT.MANEUVER_OUTCOME_NA_CASCADE_NOTES ||
		description === CONSTANT.RESET_MANEUVER_OUTCOME_NA_CASCADE_NOTES;
	if (!isCascade) return false;
	if (event.preDefined === true) return false;
	return (event.totalAttempt ?? 0) <= 0 && (event.activeAttempt ?? 0) <= 0;
}

function isNaCascadeEvent(event: OBCommentsReq): boolean {
	return event.description === CONSTANT.MANEUVER_OUTCOME_NA_CASCADE_NOTES || !!event.isDisabled;
}

function isResetNaCascadeEvent(event: OBCommentsReq): boolean {
	return (
		event.description === CONSTANT.RESET_MANEUVER_OUTCOME_NA_CASCADE_NOTES ||
		event.description === CONSTANT.RESET_NOT_APPLICABLE_NOTES
	);
}

function userLastActionFromEvent(event: OBCommentsReq): string | null {
	if (event.isObservationComment || event.isReset) return null;
	if (isNaCascadeEvent(event) || isResetNaCascadeEvent(event)) return null;
	if (isUnusedManeuverOutcomeCascadeEvent(event)) return null;
	if (event.isRemoved === true) return CONSTANT.OB_LAST_ACTION.REMOVED;
	if (event.isRemoved === false) return CONSTANT.OB_LAST_ACTION.ADDED;
	return null;
}

function lastActionForDisplayOb(
	event: OBCommentsReq,
	previousUserLastAction: string,
): { statusLastAction: string; userLastAction: string } {
	const userAction = userLastActionFromEvent(event);
	if (userAction) {
		return { statusLastAction: userAction, userLastAction: userAction };
	}
	if (isResetNaCascadeEvent(event)) {
		return { statusLastAction: previousUserLastAction, userLastAction: previousUserLastAction };
	}
	if (isNaCascadeEvent(event)) {
		return { statusLastAction: CONSTANT.OB_LAST_ACTION.DISABLED, userLastAction: previousUserLastAction };
	}
	return { statusLastAction: previousUserLastAction, userLastAction: previousUserLastAction };
}

function statusFromAddRemoveEvent(event: OBCommentsReq): DisplayObStatus | null {
	if (!event.observationCode) return null;
	if (event.isObservationComment) return null;
	if (isUnusedManeuverOutcomeCascadeEvent(event)) return null;
	if (event.isDisabled || event.description === CONSTANT.MANEUVER_OUTCOME_NA_CASCADE_NOTES) {
		return "na";
	}
	if (
		event.description === CONSTANT.RESET_MANEUVER_OUTCOME_NA_CASCADE_NOTES ||
		event.description === CONSTANT.RESET_NOT_APPLICABLE_NOTES
	) {
		return event.isRemoved === true ? "removed" : "selected";
	}
	if (event.isRemoved === true) return "removed";
	if (event.isRemoved === false) return "selected";
	return null;
}

/** Same comparator as Review `sortTs1V3SummaryCommentsByCreatedAt` (stable index tie-break). */
function sortCommentsByCreatedAt(comments: OBComments[]): OBComments[] {
	const indexed = comments.map((row, index) => ({ row, index }));
	indexed.sort((a, b) => {
		const ta = a.row.createdAt ? new Date(a.row.createdAt).getTime() : 0;
		const tb = b.row.createdAt ? new Date(b.row.createdAt).getTime() : 0;
		if (ta !== tb) return ta - tb;
		return a.index - b.index;
	});
	return indexed.map((entry) => entry.row);
}

function repeatRowKey(row: { maneuverCode?: string; repeatCount?: number; candidateId?: string }): string {
	return `${row.maneuverCode ?? ""}::${row.repeatCount ?? 0}::${row.candidateId ?? ""}`;
}

function candidateIdsForManeuver(
	rows: Array<{ maneuverCode?: string; candidateId?: string }>,
	maneuverCode: string,
): string[] {
	const ids = new Set<string>();
	for (const row of rows) {
		if (row.maneuverCode === maneuverCode && row.candidateId) ids.add(row.candidateId);
	}
	return [...ids];
}

function makeRepeatComment(params: {
	maneuverCode: string;
	count: number;
	candidateId: string;
	createdAt?: string;
	maneuver?: string;
}): OBComments {
	const { maneuverCode, count, candidateId, createdAt, maneuver } = params;
	return {
		maneuver: maneuver ?? `R${count} ${maneuverCode}`.trim(),
		ob: "REPEAT",
		remarks: "REPEAT",
		maneuverCode,
		candidateId,
		action: false,
		instructorComment: "",
		isDisabled: false,
		isRepeat: true,
		repeatCount: count,
		createdAt,
	};
}

export function buildDisplayComments(cr: CandidateRating): DisplayCommentDraft[] {
	const visible = filterTs1Step2SummaryComments(cr.selectedOBs ?? [], cr.addRemoveOb);
	const withRepeats = insertRepeatRowsFromResetManeuvers(visible, cr.resetManeuvers, cr.selectedOBs);
	return sortCommentsByCreatedAt(withRepeats).map((comment, index) =>
		mapSummaryCommentToDisplayComment(comment, index),
	);
}

function insertRepeatRowsFromResetManeuvers(
	comments: OBComments[],
	resetManeuvers: ResetManeuverItem[] | undefined,
	selectedOBs: OBComments[] | undefined,
): OBComments[] {
	const sourceRows = [...(selectedOBs ?? []), ...comments];
	const extra: OBComments[] = [];
	const existing = new Set(
		comments.filter((row) => row.ob === "REPEAT" && row.maneuverCode).map((row) => repeatRowKey(row)),
	);

	const ensureRepeat = (row: OBComments, scopedOnly = false) => {
		if (!row.maneuverCode) return;
		const count = row.repeatCount ?? 0;
		let ids: string[];
		if (row.candidateId && scopedOnly) {
			ids = [row.candidateId];
		} else if (row.candidateId) {
			const candidateIds = candidateIdsForManeuver(sourceRows, row.maneuverCode);
			if (!candidateIds.includes(row.candidateId)) {
				candidateIds.push(row.candidateId);
			}
			ids = candidateIds.length ? candidateIds : [row.candidateId];
		} else {
			const candidateIds = candidateIdsForManeuver(sourceRows, row.maneuverCode);
			ids = candidateIds;
		}
		for (const candidateId of ids) {
			const key = repeatRowKey({ maneuverCode: row.maneuverCode, repeatCount: count, candidateId });
			if (existing.has(key)) continue;
			existing.add(key);
			extra.push(
				makeRepeatComment({
					maneuverCode: row.maneuverCode,
					count,
					candidateId,
					createdAt: row.createdAt,
					maneuver: row.maneuver,
				}),
			);
		}
	};

	for (const row of comments.filter((comment) => comment.ob === "REPEAT")) {
		ensureRepeat(row);
	}

	for (const reset of resetManeuvers ?? []) {
		const count = Number(reset.count ?? 0);
		if (!reset.maneuverCode || count <= 0) continue;
		ensureRepeat(
			makeRepeatComment({
				maneuverCode: reset.maneuverCode,
				count,
				candidateId: reset.candidateId,
				createdAt: reset.createdAt,
			}),
			reset.candidateScoped === true,
		);
	}

	return finalizeDisplayComments(comments, extra);
}

function finalizeDisplayComments(comments: OBComments[], extra: OBComments[]): OBComments[] {
	const merged = extra.length ? [...comments, ...extra] : comments;
	const hasStampedRepeat = merged.some((row) => row.ob === "REPEAT" && row.candidateId);
	if (!hasStampedRepeat) return merged;
	return merged.filter((row) => row.ob !== "REPEAT" || !!row.candidateId);
}

export function buildDisplayObs(cr: CandidateRating): DisplayObDraft[] {
	const order: string[] = [];
	const last = new Map<string, DisplayObDraft>();
	const userLastActionByKey = new Map<string, string>();

	const addRemoveOb = cr.addRemoveOb ?? [];
	const resetManeuvers = cr.resetManeuvers ?? [];
	for (const event of addRemoveOb) {
		if (
			!shouldReplayDraftAddRemoveObEvent(
				{
					candidateId: event.candidateId ?? "",
					maneuverCode: event.maneuverCode ?? "",
					observationCode: event.observationCode ?? "",
					createdAt: event.createdAt ?? "",
					isRepeat: event.isRepeat,
					isReset: event.isReset,
					candidateRole: event.candidateRole,
				},
				resetManeuvers,
				addRemoveOb,
				true,
			)
		) {
			continue;
		}
		const status = statusFromAddRemoveEvent(event);
		if (!status || !event.candidateId || !event.maneuverCode || !event.observationCode) continue;

		const key = obDisplayKey(event);
		if (!last.has(key)) order.push(key);
		const previousUserLastAction = userLastActionByKey.get(key) ?? CONSTANT.OB_LAST_ACTION.NO_ACTION;
		const { statusLastAction, userLastAction } = lastActionForDisplayOb(event, previousUserLastAction);
		userLastActionByKey.set(key, userLastAction);
		last.set(key, {
			sortOrder: 0,
			candidateId: event.candidateId,
			maneuverCode: event.maneuverCode,
			observationCode: event.observationCode,
			candidateRole: event.candidateRole,
			status,
			isDisabled: status === "na",
			activeAttempt: event.activeAttempt ?? 0,
			totalAttempt: event.totalAttempt ?? 0,
			lastAction:
				userLastAction && userLastAction !== CONSTANT.OB_LAST_ACTION.NO_ACTION
					? userLastAction
					: statusLastAction === CONSTANT.OB_LAST_ACTION.DISABLED
						? CONSTANT.OB_LAST_ACTION.NO_ACTION
						: statusLastAction,
		});
	}

	return order.map((key, index) => ({
		...(last.get(key) as DisplayObDraft),
		sortOrder: index,
	}));
}

/** Rebuild last-state display arrays from the live event log. Single persist hook. */
export function rebuildDisplaySnapshots(cr: CandidateRating, presets: Ts1ObPreset[] = []): CandidateRating {
	const addRemoveOb = stripManeuverOutcomeCascadeEvents(cr.addRemoveOb);
	const next = { ...cr, addRemoveOb };
	return {
		...next,
		displayComments: buildDisplayComments(next),
		displayObs: overlayOutcomeNaOnDisplayObs(buildDisplayObs(next), next, presets),
	};
}

/** Persist display snapshots onto a TS1 PF/PM draft (v3 or v4) before IndexedDB / staging write. */
export function withTs1V3DisplaySnapshots(draft: DraftItem): DraftItem {
	if (!isTs1V3OrV4RatingFormVersion(draft?.ratingFormVersion) || !draft.candidateRating) return draft;
	return {
		...draft,
		candidateRating: rebuildDisplaySnapshots(
			draft.candidateRating,
			collectAssessmentConfigObPresets(draft.formDetails?.assessmentConfig),
		),
	};
}
